import emailjs from "@emailjs/browser";

// ─── EmailJS configuration ────────────────────────────────────────────────────
// Set these in your .env file (REACT_APP_ prefix required by Create React App)
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_CUSTOMER = process.env.REACT_APP_EMAILJS_CUSTOMER_TEMPLATE_ID; // booking confirmation → customer
const TEMPLATE_ADMIN = process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID; // new booking alert   → admin

// ─── Extras display names ─────────────────────────────────────────────────────
const EXTRAS_NAMES = {
  water: "Bottled Water",
  softdrink: "Soft Drink",
  beer: "Local Beer",
  champagne: "Champagne",
  chips: "Chips",
  nuts: "Mixed Nuts",
  crackers: "Crackers",
  pringles: "Pringles",
  carbonara: "Carbonara",
  bolognese: "Bolognese",
  alfredo: "Alfredo",
  marinara: "Marinara",
  bruschetta: "Bruschetta",
  caprese: "Caprese Salad",
  margherita: "Margherita",
  pepperoni: "Pepperoni",
  quattro: "Quattro Formaggi",
  vegetariana: "Vegetariana",
};

class EmailService {
  // ─── Helpers ───────────────────────────────────────────────────────────────
  _formatDate(date) {
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  _formatExtras(extras) {
    if (!extras) return "None";
    const all = {
      ...(typeof extras.drinks === "object" && !Array.isArray(extras.drinks)
        ? extras.drinks
        : {}),
      ...(typeof extras.snacks === "object" && !Array.isArray(extras.snacks)
        ? extras.snacks
        : {}),
      ...(typeof extras.food === "object" && !Array.isArray(extras.food)
        ? extras.food
        : {}),
    };
    const items = Object.entries(all)
      .filter(([, qty]) => Number(qty) > 0)
      .map(([id, qty]) => `${EXTRAS_NAMES[id] || id} x${qty}`);
    if (Array.isArray(extras.drinks))
      extras.drinks.forEach((d) => items.push(`${d.name} x${d.quantity}`));
    if (Array.isArray(extras.food))
      extras.food.forEach((f) => items.push(`${f.name} x${f.quantity}`));
    return items.length ? items.join(", ") : "None";
  }

  // ─── Core send via EmailJS ─────────────────────────────────────────────────
  async _send(templateId, templateParams) {
    try {
      console.log(
        `Sending [${templateId}] → to_email: ${templateParams.to_email || "(hardcoded in dashboard)"}`,
        templateParams,
      );
      await emailjs.send(SERVICE_ID, templateId, templateParams, {
        publicKey: PUBLIC_KEY,
      });
      console.log(`✅ Email sent [${templateId}]`);
      return { success: true };
    } catch (err) {
      const msg = err?.text || err?.message || String(err);
      console.error(`Email error [${templateId}]:`, msg);
      // Never throw — email failure must not break the booking flow
      return { success: false, error: msg };
    }
  }

  // ─── Customer booking confirmation ─────────────────────────────────────────
  // EmailJS template variables: {{to_name}}, {{to_email}}, {{booking_reference}},
  //   {{service_type}}, {{date}}, {{time_slot}}, {{num_guests}}, {{total_price}}, {{extras_list}}
  async sendBookingConfirmation(bookingData) {
    const {
      bookingReference,
      customerInfo,
      serviceType,
      date,
      timeSlot,
      numberOfGuests,
      totalPrice,
      extras,
    } = bookingData;
    return this._send(TEMPLATE_CUSTOMER, {
      to_name: customerInfo.name || customerInfo.fullName || "Guest",
      to_email: customerInfo.email,
      booking_reference: bookingReference,
      service_type: serviceType,
      date: this._formatDate(date),
      time_slot: timeSlot || "—",
      num_guests: String(numberOfGuests),
      total_price: `€${Number(totalPrice).toFixed(2)}`,
      extras_list: this._formatExtras(extras),
    });
  }

  // ─── Admin new-booking notification ────────────────────────────────────────
  // EmailJS template variables: {{to_email}}, {{customer_name}}, {{customer_email}},
  //   {{customer_phone}}, {{booking_reference}}, {{service_type}}, {{date}},
  //   {{time_slot}}, {{num_guests}}, {{total_price}}
  async sendAdminNotification(bookingData) {
    const {
      bookingReference,
      customerInfo,
      serviceType,
      date,
      timeSlot,
      numberOfGuests,
      totalPrice,
    } = bookingData;

    // IMPORTANT: Do NOT pass any customer email data here.
    // EmailJS scans ALL param values for email-like strings and
    // sends a copy to any address it finds, regardless of template settings.
    // The admin "To Email" is hardcoded in the EmailJS dashboard.
    return this._send(TEMPLATE_ADMIN, {
      customer_name: customerInfo.name || customerInfo.fullName || "—",
      customer_phone: customerInfo.phone || "—",
      booking_reference: bookingReference,
      service_type: serviceType,
      date: this._formatDate(date),
      time_slot: timeSlot || "—",
      num_guests: String(numberOfGuests),
      total_price: `€${Number(totalPrice).toFixed(2)}`,
    });
  }
  // ─── Customer extras-added confirmation ────────────────────────────────────
  // Uses the same customer template — sends updated extras info
  async sendExtrasAddedConfirmation(bookingData, newExtras, extrasTotal) {
    const { bookingReference, customerInfo, serviceType, date } = bookingData;
    return this._send(TEMPLATE_CUSTOMER, {
      to_name: customerInfo.name || customerInfo.fullName || "Guest",
      to_email: customerInfo.email,
      booking_reference: bookingReference,
      service_type: serviceType,
      date: this._formatDate(date),
      time_slot: "—",
      num_guests: "—",
      total_price: `€${Number(extrasTotal).toFixed(2)}`,
      extras_list: this._formatExtras(newExtras),
    });
  }

  // Kept for backward compatibility (used in admin table display)
  formatExtrasForEmail(extras) {
    return this._formatExtras(extras);
  }
}

const emailService = new EmailService();
export default emailService;
